import * as React from 'react';
import { Chart } from 'chart.js';

import { LoaderIcon } from '@cian/ui-kit/prev/icons';

import * as styles from './styles.css';

import { preparedData, preparedOptions } from './utils';
import { IDataset, TGraphValuesType } from '../../types/redux';

import { ActionWithHorizontalScroll } from '../ActionWithHorizontalScroll';
import { trackSwipe, EBlocks } from '../../analytics';

export interface IGraphProps {
  isLoading: boolean;
  labels: string[];
  datasets: IDataset[];
  graphValuesType: TGraphValuesType;
}

export class Graph extends React.Component<IGraphProps, {}> {
  private chartBlock = React.createRef<HTMLCanvasElement>();
  private graph: Chart;

  public componentDidMount() {
    /* istanbul ignore else */
    if (this.chartBlock.current) {
      const chart = this.chartBlock.current.getContext('2d');

      /* istanbul ignore else */
      if (chart) {
        const gradientTopLine = chart.createLinearGradient(0, 0, 0, 450);
        const gradiendBottomLine = chart.createLinearGradient(0, 0, 0, 450);

        gradientTopLine.addColorStop(0, 'rgba(232, 66, 96, 0.3)');
        gradientTopLine.addColorStop(0.7, 'rgba(232, 66, 96, 0)');

        gradiendBottomLine.addColorStop(0, 'rgba(4, 104, 255, 0.3)');
        gradiendBottomLine.addColorStop(1, 'rgba(4, 104, 255, 0.05)');

        const data = [
          {
            backgroundColor: gradientTopLine,
            ...this.props.datasets[0],
          },
          {
            backgroundColor: gradiendBottomLine,
            ...this.props.datasets[1],
          },
        ];

        const maxYAxisValue = Math.max(...data[0].data, ...data[1].data);
        const minYAxisValue = Math.min(...data[0].data, ...data[1].data);

        this.graph = new Chart(chart, {
          type: 'line',
          data: preparedData({ labels: this.props.labels, data }),
          options: preparedOptions(maxYAxisValue, minYAxisValue, this.props.graphValuesType),
        });
      }
    }
  }

  public componentDidUpdate() {
    /* istanbul ignore else */
    if (this.graph && this.graph.data.datasets) {
      this.graph.data.datasets = this.graph.data.datasets.map((dataset, index) => {
        return {
          ...dataset,
          ...this.props.datasets[index],
        };
      });

      this.graph.data.labels = this.props.labels;

      this.graph.options = preparedOptions(
        Math.max(...this.props.datasets[0].data, ...this.props.datasets[1].data),
        Math.min(...this.props.datasets[0].data, ...this.props.datasets[1].data),
        this.props.graphValuesType,
      );

      this.graph.update();
    }
  }

  public render() {
    const { isLoading } = this.props;

    return (
      <ActionWithHorizontalScroll action={() => trackSwipe(EBlocks.Graphs)}>
        <div className={styles['container']}>
          {isLoading && (
            <div className={styles['preloader']}>
              <LoaderIcon styles={{ color: 'currentColor' }} custom={{ size: '28' }} />
            </div>
          )}
          <div className={styles['chart-wrapper']}>
            <canvas ref={this.chartBlock} />
          </div>
        </div>
      </ActionWithHorizontalScroll>
    );
  }
}
